import type { OutboxRow } from '@/lib/idb';
import type { Outbox } from '@/lib/outbox';

/**
 * A guest added with no signal goes up from players' own queue, and a game that names that guest
 * goes up from the outbox. Both wake on the same events (online, visible, a heartbeat), so on the
 * way back the game create can reach the server first. It answers 400 VALIDATION ("somebody on that
 * roster is not in the crew"), which the outbox treats as final: the create is parked and every
 * play of the game is parked as `create_failed`.
 *
 * Once the guest is on the server that verdict is stale. This puts exactly those rows back, once.
 * It needs nothing from the module that made the game, and it never touches a create that failed
 * for any other reason.
 */

/** Rows to requeue after `sentGuestIds` reached the server. `already` is what was requeued before. */
export function parkedRowsToRetry(
  rejected: readonly OutboxRow[],
  sentGuestIds: readonly string[],
  already: ReadonlySet<string> = new Set(),
): string[] {
  if (sentGuestIds.length === 0) return [];
  const streams = new Set<string>();
  const ids: string[] = [];
  for (const r of rejected) {
    if (r.op !== 'create' || r.code !== 'validation' || already.has(r.id)) continue;
    const body = JSON.stringify(r.request?.body ?? null);
    if (!sentGuestIds.some((g) => body.includes(g))) continue;
    streams.add(`${r.kind}:${r.streamId}`);
    ids.push(r.id);
  }
  if (ids.length === 0) return [];
  for (const r of rejected) {
    if (r.op !== 'create' && r.code === 'create_failed' && streams.has(`${r.kind}:${r.streamId}`)) ids.push(r.id);
  }
  return ids;
}

const requeued = new Set<string>();

/**
 * Call after a guest flush that sent something. Waits for any flush already running (so a create
 * that is in flight has been answered and parked), then requeues the creates that were refused only
 * because the guest was not there yet. A create parked for this reason is retried once.
 */
export async function retryCreatesWaitingOnGuests(
  sentGuestIds: readonly string[],
  ob: Pick<Outbox, 'flush' | 'rejected' | 'requeue'>,
): Promise<string[]> {
  if (sentGuestIds.length === 0) return [];
  await ob.flush();
  const ids = parkedRowsToRetry(await ob.rejected(), sentGuestIds, requeued);
  if (ids.length === 0) return [];
  for (const id of ids) requeued.add(id);
  await ob.requeue(ids);
  return ids;
}
